import {
  Button,
  TextField,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
} from '@mui/material'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import axios from 'axios'
import PageAppBar from 'components/PageAppBar'
import { base_url } from 'env'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'react-hot-toast'
import dayjs, { Dayjs } from 'dayjs'

// Define the state for the form, using Dayjs for the date picker
type ProfileFormState = {
  firstName: string
  lastName: string | null
  birthday: Dayjs | null
  grade: string
  timeTarget: number // Ajouté
}

const initialProfile: ProfileFormState = {
  firstName: '',
  lastName: null,
  birthday: null,
  grade: 'CP',
  timeTarget: 60, // Ajouté: default de 60 minutes
}

const grades = [
  'PS',
  'MS',
  'GS',
  'CP',
  'CE1',
  'CE2',
  'CM1',
  'CM2',
  '6e',
  '5e',
  '4e',
  '3e',
  '2de',
  '1re',
  'Tle',
]

export default function ProfileCreationPage() {
  const navigate = useNavigate()
  const [profile, setProfile] = useState(initialProfile)
  const [isSaving, setIsSaving] = useState(false) // Ajouté pour le feedback visuel

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    const isNumeric = ['timeTarget'].includes(name)
    setProfile({
      ...profile,
      [name]: isNumeric ? Number(value) : value || null,
    })
  }

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target
    setProfile({ ...profile, [name as string]: value })
  }

  const handleDateChange = (newDate: Dayjs | null) => {
    setProfile({ ...profile, birthday: newDate })
  }

  const handleSubmit = () => {
    if (!profile.firstName || !profile.birthday || !profile.grade) {
      toast.error('Prénom, date de naissance, et classe sont obligatoires.')
      return
    }

    setIsSaving(true) // Activer le feedback visuel

    const payload: { [key: string]: any } = {
      firstName: profile.firstName,
      birthday: profile.birthday.unix(),
      grade: profile.grade,
      timeTarget: profile.timeTarget, // Ajouté
    }

    if (profile.lastName) {
      payload.lastName = profile.lastName
    }

    axios
      .post(base_url + `/profile/`, payload)
      .then((response) => {
        toast.success('Profil créé avec succès !')
        navigate(`/profile/${response.data.id}`)
      })
      .catch((error) => {
        console.error('Profile creation failed:', error)
      })
      .finally(() => {
        setIsSaving(false) // Désactiver le feedback visuel
      })
  }

  return (
    <PageAppBar pageName="Créer un Profil">
      <Box
        component="form"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
          alignItems: 'center',
          padding: '2rem',
          maxWidth: '600px',
          margin: 'auto',
        }}
      >
        <Typography variant="h5">Nouveau Profil</Typography>

        <TextField
          required
          fullWidth
          name="firstName"
          label="Prénom"
          value={profile.firstName}
          variant="outlined"
          onChange={handleChange}
        />
        <TextField
          fullWidth
          name="lastName"
          label="Nom (Optionnel)"
          value={profile.lastName || ''}
          variant="outlined"
          onChange={handleChange}
        />
        <DateTimePicker
          label="Date de naissance *"
          value={profile.birthday}
          onChange={handleDateChange}
          sx={{ width: '100%' }}
          ampm={false}
          maxDate={dayjs()}
        />
        <FormControl fullWidth required>
          <InputLabel id="grade-select-label">Classe</InputLabel>
          <Select
            labelId="grade-select-label"
            name="grade"
            value={profile.grade}
            label="Classe"
            onChange={handleSelectChange}
          >
            {grades.map((g) => (
              <MenuItem key={g} value={g}>
                {g}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          name="timeTarget"
          label="Objectif de temps de lecture (minutes)"
          type="number"
          value={profile.timeTarget}
          variant="outlined"
          onChange={handleChange}
          inputProps={{ min: 0 }}
        />

        <Button
          variant="contained"
          color="success"
          onClick={handleSubmit}
          disabled={isSaving}
          sx={{ mt: 2, minWidth: '150px' }}
        >
          {isSaving ? <CircularProgress size={24} /> : 'Créer'}
        </Button>
      </Box>
    </PageAppBar>
  )
}
